import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, TrendingUp, BookOpen, BarChart2, Brain,
  Gift, Settings, ChevronLeft, ChevronRight,
  Sun, Moon, Bell, Zap, LogOut, ShieldAlert, Shield, Users, Menu, X
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase';
import { getPlanFromCache, setPlanCache } from '../utils/planCache';
import { useTheme } from '../context/ThemeContext';
import './Layout.css';

const NAV_ITEMS = [
  { id: 'dashboard',  label: 'Dashboard',          icon: LayoutDashboard, path: '/dashboard',    tab: 'dashboard' },
  { id: 'my-trades',  label: 'My Trades',           icon: TrendingUp,      path: '/trades',       tab: 'my-trades' },
  { id: 'journal',    label: 'Psychology Tracker',  icon: Brain,           path: '/journal',      tab: 'psychology',  proRequired: true },
  { id: 'reports',    label: 'Reports',             icon: BarChart2,       path: '/reports',      tab: 'reports' },
  { id: 'ai',         label: 'AI Analysis',         icon: Brain,           path: '/ai-analysis',  tab: 'ai-insights', proRequired: true },
  { id: 'risk',       label: 'Risk Manager',        icon: ShieldAlert,     path: '/risk-manager', tab: 'risk',        proRequired: true },
  { id: 'affiliate',  label: 'Affiliate',           icon: Gift,            path: '/affiliate',    tab: null,          proRequired: true },
  { id: 'community',  label: 'Community',           icon: Users,           path: '/community',    tab: 'community' },
  { id: 'pricing',    label: 'Pricing & Plans',     icon: Zap,             path: '/pricing',      tab: null },
  { id: 'settings',   label: 'Settings',            icon: Settings,        path: '/settings',     tab: 'settings' },
];

const PAGE_TITLES = {
  '/dashboard':   'Dashboard',
  '/trades':      'My Trades',
  '/journal':     'Psychology Tracker',
  '/community':   'Community',
  '/reports':     'Reports',
  '/ai-analysis': 'AI Analysis',
  '/risk-manager': 'Risk Manager',
  '/admin':        'Admin Panel',
  '/affiliate':   'Affiliate Program',
  '/pricing':     'Pricing & Plans',
  '/settings':    'Settings',
  '/app':         'Dashboard',
};

const Layout = ({ children, currentUser, isPaidUser, onLogout }) => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Initialize from localStorage — instant render, no flash
  const [userPlan, setUserPlan] = useState(getPlanFromCache() || 'loading');

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) return;

      // Show cached plan immediately while Firestore loads
      const cached = getPlanFromCache();
      if (cached) setUserPlan(cached);

      // Fetch fresh in background and update cache
      try {
        const snap = await getDoc(doc(db, 'subscriptions', user.uid));
        const plan = snap.exists()
          ? (snap.data().planId || snap.data().plan || 'free')
          : 'free';
        setUserPlan(plan);
        setPlanCache(plan);
      } catch (_) {
        if (!cached) setUserPlan('free');
      }
    });
    return () => unsubAuth();
  }, []);

  const currentPath = location.pathname;
  const pageTitle = PAGE_TITLES[currentPath] || 'TradeFix AI';

  const handleNavClick = (item) => {
    navigate(item.path);
    // Dispatch event so AdvancedTradingAnalyzer syncs its activeTab
    if (item.tab) {
      window.dispatchEvent(new CustomEvent('tradefix-navigate', { detail: item.tab }));
    }
    setMobileOpen(false);
  };

  const isActive = (item) => {
    if (item.path === '/dashboard' || item.path === '/app') {
      return currentPath === '/dashboard' || currentPath === '/app';
    }
    return currentPath === item.path;
  };

  const initials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name[0].toUpperCase();
  };

  return (
    <div className="app-layout" data-theme={theme}>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="sidebar-overlay"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">T</div>
          <span className="sidebar-logo-text">TradeFix AI</span>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {NAV_ITEMS.map((item) => {
            const isLocked = item.proRequired && userPlan === 'free';
            return (
              <button
                key={item.id}
                className={`nav-item ${isActive(item) ? 'active' : ''}`}
                onClick={() => handleNavClick(item)}
                title={collapsed ? item.label : undefined}
              >
                <item.icon size={18} />
                <span className="nav-item-label">{item.label}</span>
                {isLocked && !collapsed && (
                  <span style={{
                    marginLeft: 'auto',
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                    padding: '1px 5px',
                    borderRadius: 4,
                    background: 'var(--accent-subtle)',
                    color: 'var(--accent)',
                    border: '1px solid var(--accent)',
                  }}>PRO</span>
                )}
              </button>
            );
          })}
          {currentUser?.email === 'krishautomation.services@gmail.com' && (
            <button
              className={`nav-item ${currentPath === '/admin' ? 'active' : ''}`}
              onClick={() => {
                navigate('/admin');
                window.dispatchEvent(new CustomEvent('tradefix-navigate', { detail: 'admin' }));
              }}
              title={collapsed ? 'Admin Panel' : undefined}
            >
              <Shield size={18} />
              <span className="nav-item-label">Admin Panel</span>
            </button>
          )}
        </nav>

        {/* Bottom section */}
        <div className="sidebar-bottom">
          {/* Theme toggle */}
          <button className="theme-toggle-btn" onClick={toggleTheme} title={collapsed ? (theme === 'dark' ? 'Light mode' : 'Dark mode') : undefined}>
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            <span className="nav-item-label">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          </button>

          {/* User card */}
          <div className="user-card">
            <div className="user-avatar">
              {currentUser?.photoURL
                ? <img src={currentUser.photoURL} alt="avatar" />
                : initials(currentUser?.name || currentUser?.email)}
            </div>
            <div className="user-info">
              <div className="user-name">{currentUser?.name || 'User'}</div>
              <div className="user-email">{currentUser?.email}</div>
            </div>
          </div>

          {/* Logout */}
          <button className="logout-btn" onClick={onLogout} title={collapsed ? 'Logout' : undefined}>
            <LogOut size={18} />
            <span className="nav-item-label">Logout</span>
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          className="collapse-btn"
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>

      {/* ── Main area ── */}
      <div className="main-area">
        {/* Topbar */}
        <header className="layout-topbar">
          <div className="topbar-left">
            {/* Mobile hamburger — hidden on desktop via CSS */}
            <button
              className="hamburger-btn"
              onClick={() => setMobileOpen(o => !o)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <span className="topbar-page-title">{pageTitle}</span>
          </div>
          <div className="topbar-right">
            <button className="bell-btn" title="Notifications">
              <Bell size={18} />
            </button>
            {userPlan === 'loading' ? (
              <div className="skeleton-badge" />
            ) : (
              <div className="topbar-plan-badge">
                <span className="plan-dot" />
                {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)}
              </div>
            )}
            {!isPaidUser && (
              <button
                className="upgrade-btn"
                onClick={() => navigate('/pricing')}
              >
                <Zap size={14} />
                Upgrade to Pro
              </button>
            )}
          </div>
        </header>

        {/* Content */}
        <main className="layout-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
